<template>
  <v-form :disabled="project.readonly">
    <v-card>
      <v-card-title>
        <h3>現地における目視調査</h3>
      </v-card-title>
      <v-tabs
        v-model="tabIndex"
        background-color="green lighten-2"
        light
        center-active
        align-with-title
        centered
        show-arrows
        slider-size="5"
        grow
        @change="onTabChanged">
        <v-tabs-slider color="pink darken-2"></v-tabs-slider>
        <v-tab
          v-for="(room, rid) in project.rooms"
          :key="room.id"
          v-bind="diffAttributes(previousObject.rooms ? previousObject.rooms[rid] : null, room)"
          :disabled="!!room.disable_field_survey"
          class="d-block">
          <div class="text-caption">[{{ room.room_no }}]</div>
          {{ room.field_room_name }}
        </v-tab>
      </v-tabs>
      <v-tabs-items
        v-model="tabIndex">
        <v-tab-item
          v-for="(room, roomIndex) in project.rooms"
          :key="`room-${roomIndex}`"
          class="tab">
          <room-header
            v-model="project.rooms[roomIndex]"
            :facility-name="project.facility_name" />
          <room-row
            v-for="(part,index) of room.parts"
            :key="`part-${index}`"
            :statuses="statuses"
            :reasons="reasons"
            :samplings="samplings"
            :results="results"
            :sampling-results="samplingResults"
            :item="part"
            :index="index"
            :room-index="roomIndex"
            :remarks-options="fieldRemarksOptions"
            :error="error"
            :readonly="project.readonly"
            :previous-object="previousParts(index)"
            :created="!!project.field_survey_created_at"
            :applied="!!project.field_survey_apply_status"
            :written-field-id="writtenFieldId"
            :diff-disabled="disableDiff"
            @cycle="onWrittenInfoCycleClicked" />
          <v-card v-if="previousObject.rooms && previousObject.rooms[roomIndex]">
            <v-card-text
              v-bind="diffAttributes(previousObject.rooms
                ? previousObject.rooms[roomIndex].remarks : null, room.remarks)">
              <v-textarea
                v-model="room.remarks"
                class="ma-3"
                outlined
                label="特記事項"
                rows="3" />
              <div
                v-if="hasDiff(previousObject.rooms
                  ? previousObject.rooms[roomIndex].remarks : null, room.remarks)">
                {{ previousValue(previousObject.rooms
                  ? previousObject.rooms[roomIndex].remarks : null, room.remarks) }}
              </div>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
    <global-footer>
      <template #left>
        <v-btn
          large
          @click.prevent="onReturnClicked">
          <v-icon>mdi-chevron-left</v-icon>
          戻る
        </v-btn>
      </template>
      <template
        v-if="!project.readonly"
        #center>
        <v-btn
          large
          color="primary"
          @click.prevent="onRoomUpdated">
          部位を追加する
        </v-btn>
        <v-dialog
          v-if="currentParts.length > 0"
          v-model="dialog"
          scrollable
          max-width="500px">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="ml-5"
              color="error"
              large
              v-bind="attrs"
              v-on="on"
              @click.prevent="selectedParts = []">
              部位を削除する
            </v-btn>
          </template>
          <v-card>
            <v-card-title>部位の選択</v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pb-5">
              <v-alert
                v-if="project.rooms[tabIndex]"
                class="warning"
                dark
                dense>
                選択した部位を削除します。（書面調査の情報も削除されます）
                この操作は元に戻せません。
              </v-alert>
              <v-checkbox
                v-for="(part, index) in currentParts"
                :key="index"
                v-model="selectedParts"
                :label="part.name"
                :value="part"
                :hide-details="true"
                multiple
                dense />
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-btn
                color="blue darken-1"
                text
                @click="dialog = false">
                キャンセル
              </v-btn>
              <v-spacer />
              <v-btn
                color="error"
                :disabled="selectedParts.length === 0"
                @click="onDeleteCardClicked">
                削除する
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-dialog
          v-model="copyRoomDialogIsShown"
          scrollable
          max-width="800px"
          @click:outside="onCloseCopyDialog"
          @close="onCloseCopyDialog">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              class="ml-5"
              color="success"
              large
              v-bind="attrs"
              v-on="on">
              <v-icon>mdi-clipboard</v-icon>
              他の部屋にコピーする
            </v-btn>
          </template>
          <v-card
            v-if="project.rooms[tabIndex]">
            <v-card-title>
              <h3>コピー先の部屋を選択</h3>
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="pb-5">
              <v-alert
                v-if="project.rooms[tabIndex]"
                class="info"
                dark
                dense>
                {{ project.rooms[tabIndex].field_room_name }} の現地調査結果の情報を選択した部屋にコピーします。
              </v-alert>
              <v-row>
                <v-col
                  class="text-center"
                  cols="5">
                  <v-chip color="primary">コピー元</v-chip>
                  <div class="pt-2 pb-1">
                    <h3>{{ project.rooms[tabIndex].field_room_name }}</h3>
                  </div>
                  <div
                    v-for="(part, index) in project.rooms[tabIndex].parts"
                    :key="index"
                    class="pt-2">
                    {{ part.name }}
                  </div>
                </v-col>
                <v-col cols="2">
                  <v-icon>mdi-arrow-right</v-icon>
                  <v-icon>mdi-arrow-right</v-icon>
                  <v-icon>mdi-arrow-right</v-icon>
                </v-col>
                <v-col cols="5">
                  <div class="text-center">
                    <v-chip color="orange">コピー先</v-chip>
                    <div
                      class="text--darken-1"
                      :class="{ 'error--text': selectedRooms.length >= 5 }">
                      (最大5つまで選択可能)
                    </div>
                  </div>
                  <v-checkbox
                    v-for="(room, copyRoomIndex) in project.rooms.filter((room, i) => i!==tabIndex)"
                    :key="copyRoomIndex"
                    v-model="selectedRooms"
                    multiple
                    :label="room.field_room_name"
                    :value="room"
                    @change="onChangeSelectedRoomsCheck" />
                </v-col>
              </v-row>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-btn
                color="blue darken-1"
                text
                @click="copyRoomDialogIsShown = false">
                キャンセル
              </v-btn>
              <v-spacer />
              <v-btn
                color="primary"
                :disabled="selectedRooms.length === 0"
                @click="onCopyPartsClicked">
                コピーする
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </template>
      <template #right>
        <v-btn
          v-if="!project.readonly"
          large
          color="default"
          class="mr-1"
          :disabled="!hasChanges"
          @click.prevent="onDraftSaveClicked">
          ブラウザに一時保存
        </v-btn>
        <v-btn
          v-if="!project.readonly"
          large
          color="primary"
          @click.prevent="onNextClicked">
          次へ
          <v-icon right>mdi-chevron-right</v-icon>
        </v-btn>
        <v-btn
          v-else
          large
          color="warning"
          @click.prevent="onFinishClicked">
          修了
          <v-icon right>mdi-exit-run</v-icon>
        </v-btn>
      </template>
    </global-footer>
  </v-form>
</template>

<script>
import RoomRow from './RoomRow'
import {
  getStepSession, markAsUnsavedSession, setStepSession,
} from '../../logic/session'
import RoomHeader from '../Detail/RoomHeader'
import PreviousDiff from '../../mixins/PreviousDiff'

export default {
  name: 'FieldFormStep3',
  components: {
    RoomHeader,
    RoomRow,
  },
  mixins: [PreviousDiff],
  props: {
    statuses: {
      type: Array,
      default: () => ([]),
    },
    reasons: {
      type: Array,
      default: () => ([]),
    },
    samplings: {
      type: Array,
      default: () => ([]),
    },
    results: {
      type: Array,
      default: () => ([]),
    },
    samplingResults: {
      type: Array,
      default: () => ([]),
    },
    previousObject: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      project: {
        title: '',
        rooms: [
        ],
      },
      selectedRooms: [],
      copyRoomDialogIsShown: false,
      selectedParts: [],
      dialog: false,
      tabIndex: 0,
      writtenFieldId: 0,
      error: {},
      hasChanges: false,
    }
  },
  computed: {
    currentPartNames() {
      if (!this.project.rooms[this.tabIndex]) {
        return []
      }
      return this.project.rooms[this.tabIndex].parts.map(part => part.name)
    },
    currentParts() {
      if (!this.project.rooms[this.tabIndex]) {
        return []
      }
      return this.project.rooms[this.tabIndex].parts
    },
    fieldRemarksOptions() {
      return this.project.rooms.reduce((total, room) => [
        ...total,
        ...room.parts.reduce((partsTotal, part) => [
          ...partsTotal,
          ...part.surveys.reduce((surveysTotal, survey) => {
            if (survey.field_remarks === null) {
              return surveysTotal
            }

            return [
              ...surveysTotal,
              survey.field_remarks,
            ]
          }, []),
        ], []),
      ], []).filter(item => item)
    },
  },
  watch: {
    hasChanges() {
      this.$emit('hasChange', this.hasChanges)
    },
  },
  mounted() {
    this.loadItem()
  },
  methods: {
    loadItem() {
      const project = getStepSession('field-survey', 1)
      if (project) {
        this.project = project
      }
      this.$watch('project', () => {
        this.hasChanges = true
      }, { deep: true })

      const availableRooms = this.project.rooms.filter(r => !r.disable_field_survey)
      const firstRoom = availableRooms[0]
      this.tabIndex = this.project.rooms.indexOf(firstRoom)
    },
    async validateItem() {
      try {
        const { rooms } = this.project
        await window.axios.post('/api/field-surveys/step3', { rooms })
        this.error = {}
        return true
      } catch (e) {
        if (e.response.status === 422) {
          this.error = e.response.data.errors
          const [errorKey] = Object.keys(this.error)
          const [, roomIndex] = errorKey.split('.')
          this.tabIndex = roomIndex
        }
      }
      return false
    },
    async onRoomUpdated() {
      this.project.rooms[this.tabIndex].parts.push({
        _index: this.currentParts.length,
        name: 'その他',
        order: this.project.rooms.length + 1,
        renovation_year: null,
        surveys: [],
      })
      await this.$nextTick()
      this.scrollBottom()
    },
    onDeleteCardClicked() {
      this.selectedParts.forEach(part => {
        const index = this.project.rooms[this.tabIndex].parts.indexOf(part)
        this.project.rooms[this.tabIndex].parts.splice(index, 1)
      })
      this.dialog = false
      this.selectedParts = []
    },
    scrollBottom() {
      const a = document.documentElement
      const y = a.scrollHeight - a.clientHeight
      window.scroll(0, y)
    },
    onCopyPartsClicked() {
      this.copyRoomDialogIsShown = false
      const sourceIndex = this.tabIndex
      this.selectedRooms.forEach((selectedRoom) => {
        const destinationIndex = this.project.rooms.indexOf(selectedRoom)
        // コピー元の部位をコピー先に複製
        const { parts } = this.project.rooms[sourceIndex]
        parts.forEach((part, index) => {
          // リアクティブオブジェクトがなければ作る
          if (!this.project.rooms[destinationIndex].parts[index]) {
            this.$set(this.project.rooms[destinationIndex].parts, index, { ...part })
          }
          // コピー先部位
          const destinationPart = this.project.rooms[destinationIndex].parts[index]
          const { surveys } = part ?? []
          const targetKeys = [
            'field_material',
            'field_remarks',
            'field_sampling',
            'field_status_id',
            'sampling_result',
            'is_consistent',
            'field_vs_no',
            'field_sampling_id',
          ]
          // 現地調査結果だけをコピー
          surveys.forEach((survey, sIndex) => {
            if (!destinationPart.surveys[sIndex]) {
              this.$set(destinationPart.surveys, sIndex, {})
            }
            targetKeys.forEach(key => this.$set(destinationPart.surveys[sIndex], key, survey[key]))
          })
          // コピー先の方が多い場合は、多い分の調査結果を削除
          if (destinationPart.surveys.length > surveys.length) {
            for (
              let sIndex = surveys.length;
              sIndex < destinationPart.surveys.length;
              sIndex += 1
            ) {
              // 書類調査結果がある場合は現地調査項目のみ空白
              if (destinationPart.surveys[sIndex].id) {
                targetKeys.forEach(key => this.$set(destinationPart.surveys[sIndex], key, ''))
              } else {
                targetKeys.forEach(key => this.$delete(destinationPart.surveys[sIndex], key))
              }
            }
          }
        })
      })
      this.tabIndex = this.project.rooms.indexOf(this.selectedRooms[this.selectedRooms.length - 1])
      this.selectedRooms = []
    },
    onReturnClicked() {
      this.$emit('change', 2)
    },
    async onNextClicked() {
      if (this.project.readonly) {
        this.$emit('change', 1)
        return
      }
      if (await this.validateItem()) {
        if (this.hasChanges) {
          markAsUnsavedSession('field-survey')
        }
        setStepSession('field-survey', 1, this.project)
        setStepSession('field-survey', 3, this.project.id)
        this.$emit('change', 4)
      }
    },
    onFinishClicked() {
      this.$router.push({ name: 'index' })
    },
    onWrittenInfoCycleClicked() {
      this.writtenFieldId = this.writtenFieldId < 5 ? this.writtenFieldId + 1 : 0
    },
    onChangeSelectedRoomsCheck() {
      if (this.selectedRooms.length > 5) {
        this.selectedRooms.pop()
      }
    },
    onCloseCopyDialog() {
      this.copyRoomDialogIsShown = false
      this.selectedRooms = []
    },
    onDraftSaveClicked() {
      setStepSession('field-survey', 1, this.project)
      markAsUnsavedSession('field-survey')
      this.hasChanges = false
    },
    previousParts(index) {
      if (!this.previousObject || !this.previousObject.rooms
        || !this.previousObject.rooms[this.tabIndex]
        || !this.previousObject.rooms[this.tabIndex].parts) {
        return {}
      }
      return this.previousObject.rooms[this.tabIndex].parts[index] ?? {}
    },
    hasDiff(previous, current) {
      if (this.$store.getters.hasPendingAjax || this.disableDiff) {
        return false
      }
      if (previous === undefined) {
        return false
      }
      if (!this.project.field_survey_apply_status) {
        return false
      }
      return !this.project.field_survey_created_at
        ? false
        : this.$super(PreviousDiff).hasDiff(previous, current)
    },
    onTabChanged() {
      this.disableDiff = true
      setTimeout(() => { this.disableDiff = false }, 400)
    },
  },
}
</script>

<style scoped>
.v-tabs-items {
  background-color: #F4F5F7;
}
.v-label {
  padding-left: 5px !important;
}
.v-application .red--text {
  margin-top: 10px !important;
}
</style>
